import {
  displayTime,
  displayDateWithWeekday,
  timezoneName,
  wrangleMoment,
} from './date';
import type {IEvent} from '../types/IEvent';
import type {IEventTemplate} from '../types/IEventTemplate';
import type {SequenceItem} from '../types/IEventTemplateMetadata';

// Types for the formatted output
export interface FormattedEventItem {
  type: 'round';
  name: string;
  eventEnd: Date;
  timezone: string;
  location: string;
  eventStart: Date;
  roundNumber: number;
  formattedDate: string;
  formattedTimeRange: string;
}

export interface FormattedBreakItem {
  type: 'break';
  duration: {
    amount: number;
    unit: string;
  };
  breakStart: Date;
  breakEnd: Date;
  formattedTimeRange: string;
}

export interface FormattedDayItem {
  type: 'day';
  dayNumber: number;
  roundsAndBreaks: Array<FormattedEventItem | FormattedBreakItem>;
}

export interface FormattedGapItem {
  type: 'gap';
  amount: number;
  unit: string;
  label: string;
}

export type FormattedSequenceItem = FormattedDayItem | FormattedGapItem;

export const formatSequentialBookingData = (
  eventTemplate: IEventTemplate,
  events: IEvent[],
  language: string = 'en',
): FormattedSequenceItem[] => {
  const sequence = eventTemplate?.metadata?.sequence;
  if (!sequence || !Array.isArray(sequence)) {
    return [];
  }

  let currentDayNumber = 0;
  let currentRoundNumber = 0;
  let previousEventEndTime: Date | null = null;
  const formattedSchedule: FormattedSequenceItem[] = [];

  for (const sequenceEntry of sequence) {
    if (sequenceEntry.type === 'day') {
      currentDayNumber += 1;
      const roundsAndBreaks: Array<FormattedEventItem | FormattedBreakItem> =
        [];

      // Process each round or break in the day
      for (const scheduleEntry of sequenceEntry.value) {
        if (scheduleEntry.type === 'round') {
          currentRoundNumber += 1;

          // Find the corresponding event and round info
          const event = events.find(
            (e) =>
              e.scheduled_from_template_id === scheduleEntry.value.template_id,
          );
          const roundInfo = eventTemplate.sequential_rounds?.find(
            (r) => r.id === scheduleEntry.value.template_id,
          );

          if (event && roundInfo) {
            const eventStart = wrangleMoment(event.event_start);
            const eventEnd = wrangleMoment(event.event_end);

            const formattedDate = displayDateWithWeekday(eventStart, language);
            const formattedStartTime = displayTime(eventStart, language);
            const formattedEndTime = displayTime(eventEnd, language);
            const formattedTimeRange = `${formattedStartTime} - ${formattedEndTime}`;
            const timezone = timezoneName(event.event_start, language) || '';

            roundsAndBreaks.push({
              type: 'round',
              roundNumber: currentRoundNumber,
              name: roundInfo.name,
              eventStart,
              eventEnd,
              formattedDate,
              formattedTimeRange,
              timezone,
              location: event.location || '',
            });

            // Update previous event end time for break calculation
            previousEventEndTime = eventEnd;
          }
        } else if (scheduleEntry.type === 'break') {
          const breakDuration = {
            amount: scheduleEntry.value.amount,
            unit: scheduleEntry.value.unit,
          };

          if (previousEventEndTime) {
            // Calculate break times based on previous event end time
            const breakStart: Date = wrangleMoment(previousEventEndTime);
            const breakDurationMinutes = convertToMinutes(
              breakDuration.amount,
              breakDuration.unit,
            );
            const breakEnd: Date = wrangleMoment(
              breakStart.getTime() + breakDurationMinutes * 60 * 1000,
            );

            // Format break time range using Intl API
            // 'h:mm A' → "2:30 PM"
            const formattedStartTime = displayTime(breakStart, language);
            const formattedEndTime = displayTime(breakEnd, language);
            const formattedTimeRange = `${formattedStartTime}-${formattedEndTime}`;

            roundsAndBreaks.push({
              type: 'break',
              duration: breakDuration,
              breakStart,
              breakEnd,
              formattedTimeRange,
            });

            // Update previous event end time for next round/break calculation
            previousEventEndTime = breakEnd;
          } else {
            // If no previous event, add break without calculated times
            roundsAndBreaks.push({
              type: 'break',
              duration: breakDuration,
              breakStart: new Date(),
              breakEnd: new Date(),
              formattedTimeRange: '',
            });
          }
        }
      }

      formattedSchedule.push({
        type: 'day',
        dayNumber: currentDayNumber,
        roundsAndBreaks,
      });
    } else if (sequenceEntry.type === 'gap') {
      // Format gap label for display
      const gapLabel = `${sequenceEntry.value.amount} ${sequenceEntry.value.unit} Gap`;

      formattedSchedule.push({
        type: 'gap',
        amount: sequenceEntry.value.amount,
        unit: sequenceEntry.value.unit,
        label: gapLabel,
      });

      // Reset previous event end time after gap (next day starts fresh)
      previousEventEndTime = null;
    }
  }

  return formattedSchedule;
};

export const convertToMinutes = (amount: number, unit: string): number => {
  const unitLower = unit.toLowerCase();
  switch (unitLower) {
    case 'minute':
    case 'minutes':
      return amount;
    case 'hour':
    case 'hours':
      return amount * 60;
    case 'day':
    case 'days':
      return amount * 24 * 60;
    default:
      return amount; // Default to treating as minutes
  }
};

export interface MeetingStats {
  totalHours: number;
  totalMinutes: number;
  totalRounds: number;
  totalBreaks: number;
  totalDays: number;
  isMultiDay: boolean;
}

// Helper Functions
export const calculateMeetingStats = (
  eventTemplate: IEventTemplate,
): MeetingStats => {
  const {sequence = []} = eventTemplate?.metadata || {};
  let totalMinutes = 0;
  let totalRounds = 0;
  let totalBreaks = 0;
  let totalDays = 0;

  sequence.forEach((item) => {
    if (item.type === 'day') {
      totalDays += 1;
      item.value?.forEach((dayItem) => {
        if (dayItem.type === 'round') {
          totalRounds += 1;
          const roundInfo = eventTemplate.sequential_rounds?.find(
            (r) => r.id === dayItem.value.template_id,
          );
          if (roundInfo) {
            totalMinutes += convertToMinutes(
              roundInfo.duration.amount,
              roundInfo.duration.unit,
            );
          }
        } else if (dayItem.type === 'break') {
          totalBreaks += 1;
          totalMinutes += convertToMinutes(
            dayItem.value.amount,
            dayItem.value.unit,
          );
        }
      });
    }
  });

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return {
    totalHours: hours,
    totalMinutes: minutes,
    totalRounds,
    totalBreaks,
    totalDays,
    isMultiDay: totalDays > 1,
  };
};

export const formatDuration = (
  hours: number,
  minutes: number,
  t: (key: string, fallback: string) => string,
): string => {
  if (hours > 0 && minutes > 0) {
    return `${hours}${t('hrs', 'hrs')} ${minutes} ${t('mins', 'mins')}`;
  }
  if (hours > 0) {
    return `${hours}${t('hrs', 'hrs')}`;
  }
  return `${minutes} ${t('mins', 'mins')}`;
};

export const calculateDayNumbers = (
  sequence: SequenceItem[],
): Map<number, number> => {
  const dayNumberMap = new Map<number, number>();
  let dayCount = 0;

  sequence.forEach((item, index) => {
    if (item.type === 'day') {
      dayCount += 1;
      dayNumberMap.set(index, dayCount);
    }
  });

  return dayNumberMap;
};

/**
 * Calculate total duration (in minutes) for a specific day in a sequential interview template
 * @param eventTemplate - The event template with sequence metadata
 * @param sequenceNumber - The sequence number of the day (0-based index)
 * @returns Total duration in minutes, or 0 if not found or invalid
 */
export const calculateDayDuration = (
  eventTemplate: IEventTemplate | null | undefined,
  sequenceNumber: number,
): number => {
  if (!eventTemplate?.metadata?.sequence) {
    return 0;
  }

  const sequence = eventTemplate.metadata.sequence;
  let dayCount = 0;
  let totalMinutes = 0;

  for (const item of sequence) {
    if (item.type === 'day') {
      if (dayCount === sequenceNumber) {
        // This is the target day, calculate its total duration
        if (item.value) {
          for (const dayItem of item.value) {
            if (dayItem.type === 'round') {
              const roundInfo = eventTemplate.sequential_rounds?.find(
                (r) => r.id === dayItem.value.template_id,
              );
              if (roundInfo) {
                totalMinutes += convertToMinutes(
                  roundInfo.duration.amount,
                  roundInfo.duration.unit,
                );
              }
            } else if (dayItem.type === 'break') {
              totalMinutes += convertToMinutes(
                dayItem.value.amount,
                dayItem.value.unit,
              );
            }
          }
        }
        break;
      }
      dayCount++;
    }
  }

  return totalMinutes;
};
